import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * Flexible Button component with multiple variants and sizes
 * Supports loading state, disabled state, and custom styling
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  className = '',
  onClick,
  type = 'button',
  ...props
}) {
  const { t } = useTranslation('common');

  // Variant styles mapping using semantic color tokens from Tailwind config
  const variantStyles = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white',
    secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-900',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    ghost: 'bg-transparent hover:bg-gray-100 text-gray-700'
  };

  // Size styles mapping
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  // Combine all styles
  const buttonClassName = `
    ${variantStyles[variant]}
    ${sizeStyles[size]}
    rounded-lg font-medium transition-colors
    disabled:opacity-50 disabled:cursor-not-allowed
    inline-flex items-center justify-center gap-2
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <button
      type={type}
      className={buttonClassName}
      disabled={disabled || loading}
      onClick={onClick}
      aria-disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <>
          <Spinner size={size} />
          <span className="sr-only">{t('button.loading')}</span>
        </>
      )}
      {children}
    </button>
  );
}

/**
 * Simple CSS-only spinner component
 */
function Spinner({ size }) {
  const spinnerSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  return (
    <span
      className={`${spinnerSizes[size]} animate-spin inline-block border-2 border-current border-t-transparent rounded-full`}
      aria-hidden="true"
    />
  );
}

// PropTypes validation
Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'ghost']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset'])
};

Spinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg']).isRequired
};